import { FC } from 'react';
import { GetServerSideProps } from 'next';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import {
	AccountsPrivacyOptionsEnum,
	EventInstanceSortEnum,
	ListWithTotals,
	ShortEventInstanceDto,
} from '@sweatpals/shared';
import { GetServerSidePropsResult } from 'next/types';
import dynamic from 'next/dynamic';
import { EventTabsEnum } from '../../components/pages/EventPage/event-page.component';
import { HostTabsEnum } from '../../components/pages/HostPage/host-page.component';
import { getLocation, LocationOptionsMap, queryKeys } from '../../constants';
import { eventsService, eventTicketsReservationService, usersService } from '../../services';
import { EventResponseType, EventType } from '../../services/types/event-types';
import { UserDataType } from '../../services/types/user-types';
import { prepareNextJsProps } from '../../utils/nextjs.utils';
import { EventTicketsPage } from '../../components/pages/EventTicketsPage/event-tickets-page.component';
import { getEventSEOMeta, getHostSEOMeta, OGMetaProps } from '../../components/OGMeta';
import { checkUserAgentIsBot } from '../../utils/checkUserAgentIsBot';
import { prefetchGetPublicEventInstanceByAliasQuery, prefetchGetPublicUserByUserNameQuery } from '../../hooks/queries';

const EventPage = dynamic(async () => import('../../components/pages/EventPage/event-page.component'));
const EventsPage = dynamic(async () => import('../../components/pages/EventsPage/events-page.component'));
const HostPage = dynamic(async () => import('../../components/pages/HostPage/host-page.component'));

const getLocationData = ({
	locationPath,
	locationState,
}: {
	locationPath?: string | null;
	locationState?: string | null;
}) => {
	const foundLocation = getLocation<null>({ path: locationPath, id: locationState }, null);

	return {
		foundLocation,
		cityId: foundLocation?.cityId,
		isOtherLocation: foundLocation?.cityId === LocationOptionsMap.others.cityId,
	};
};

export const getInstanceOfEventByAlias = async (eventAlias: string): Promise<{ eventAlias: string; shortLocalInstance: string | null }> => {
	const queryClient = new QueryClient();

	// Rewriten to use /api/events/event-filter-options/alias/:alias
	// picking first option from the list
	// it will be - nearest upcomming or latest past event

	await queryClient.prefetchQuery(
		queryKeys.relatedEvents(eventAlias, 'options'),
		async () => eventsService.getPublicEventOptionsByAlias(eventAlias, {
			pageSize: 1,
		})
	);

	const optionsResponse = queryClient.getQueryData<ListWithTotals<ShortEventInstanceDto>>(queryKeys.relatedEvents(eventAlias, 'options'));

	if (optionsResponse && optionsResponse.list?.[0]) {
		const option = optionsResponse.list?.[0];

		return { eventAlias: option.alias, shortLocalInstance: option.shortLocalInstance };
	}

	return { eventAlias, shortLocalInstance: null };
};

export const preloadEventData = async (
	eventAlias: string,
	shortLocalInstance: string,
	isBot: boolean
): Promise<GetServerSidePropsResult<Record<string, any>>> => {
	if (!isBot) {
		return {
			props: prepareNextJsProps({
				eventAlias,
				shortLocalInstance,
				isBot,
			}) satisfies LocationPageProps,
		};
	}

	const queryClient = new QueryClient();

	await Promise.all([
		prefetchGetPublicEventInstanceByAliasQuery(queryClient, eventAlias, shortLocalInstance, '[...params].1'),
		queryClient.prefetchQuery(
			queryKeys.relatedEvents(eventAlias),
			async () => eventsService.getPublicExtendEventsList({ eventAliases: [eventAlias], limit: 4, withUnverifiedEvents: true })
		),
	]);

	const event = queryClient.getQueryData<EventType>(queryKeys.eventInstance(eventAlias, shortLocalInstance));
	const eventInstances = queryClient.getQueryData<EventResponseType>(queryKeys.relatedEvents(eventAlias));

	if (!event) {
		return {
			notFound: true,
		};
	}

	const eventId = event.baseId ?? event.id;
	const instance = event && new Date(event.instance ?? event.startDate).toISOString();

	await Promise.all([
		queryClient.prefetchQuery(
			queryKeys.eventTicketsCountAndSpots(eventId, instance),
			async () => eventTicketsReservationService.getEventTicketsCountAndSpotsWithReservations(event.baseId, instance)
		),
		queryClient.prefetchQuery(
			queryKeys.userEventTicketReservations(eventId, instance),
			async () => eventTicketsReservationService.getUserEventTicketReservations(event.baseId, instance)
		),
	]);

	const seo: OGMetaProps = getEventSEOMeta(event, eventInstances?.data);

	return {
		props: prepareNextJsProps({
			dehydratedState: dehydrate(queryClient),
			eventAlias,
			shortLocalInstance,
			eventId: event.id,
			eventBaseId: event.baseId,
			instance: event.instance instanceof Date ? event.instance.toISOString() : event.instance,
			seo,
			isBot,
		}) satisfies LocationPageProps,
	};
};

export const preloadHostData = async (
	userName: string,
	isBot: boolean,
	defaultHostTab?: HostTabsEnum
): Promise<GetServerSidePropsResult<Record<string, any>>> => {
	if (!isBot) {
		return {
			props: prepareNextJsProps({
				userName,
				defaultHostTab,
				isBot,
			}) satisfies LocationPageProps,
		};
	}

	const queryClient = new QueryClient();

	const currentMonthId = format(new Date(), 'yyyy-MM');

	await prefetchGetPublicUserByUserNameQuery(queryClient, userName);

	const host = queryClient.getQueryData(queryKeys.user(userName)) as UserDataType;

	if (
		!host ||
		!host.isCommunityProfile || // only community profile pages are showed
		host.accountPrivacyOptionId !== AccountsPrivacyOptionsEnum.Public ||
		host.isTempUser
	) {
		return {
			redirect: {
				destination: '/events',
				permanent: false,
			},
		};
	}

	await Promise.all([
		queryClient.prefetchQuery(queryKeys.eventsDates(host.id, currentMonthId), async () => {
			const periodFrom = parseISO(currentMonthId);
			const periodTo = parseISO(currentMonthId);

			periodFrom.setMonth(periodFrom.getMonth() - 1);
			periodTo.setMonth(periodTo.getMonth() + 1);

			return eventsService.getPublicEventsDates({
				creatorsIds: [host.id],
				periodFrom: startOfMonth(periodFrom),
				periodTo: endOfMonth(periodTo),
				withUnverifiedEvents: true,
			});
		}),
		queryClient.prefetchInfiniteQuery(queryKeys.relatedEvents(host.id, 'upcoming-events'), async () =>
			eventsService.getPublicExtendEventsList({
				creatorsIds: [host.id],
				limit: 10,
				sort: EventInstanceSortEnum.InstanceAsc,
				withUnverifiedEvents: true,
			})
		),
	]);

	const seo: OGMetaProps = getHostSEOMeta(host);

	return {
		props: prepareNextJsProps({
			dehydratedState: dehydrate(queryClient),
			userName,
			userId: host.id,
			defaultHostTab,
			seo,
			isBot,
		}) satisfies LocationPageProps,
	};
};

export const preloadTicketsData = async (
	eventAlias: string,
	shortLocalInstance: string,
	defaultEventTab: EventTabsEnum = EventTabsEnum.TICKETS
): Promise<GetServerSidePropsResult<Record<string, any>>> => {
	const queryClient = new QueryClient();

	await prefetchGetPublicEventInstanceByAliasQuery(queryClient, eventAlias, shortLocalInstance, '[...params].2');

	const event = queryClient.getQueryData<EventType>(queryKeys.eventInstance(eventAlias, shortLocalInstance));

	if (!event) {
		return {
			notFound: true,
		};
	}

	return {
		props: prepareNextJsProps({
			eventAlias,
			shortLocalInstance,
			defaultEventTab,
		}) satisfies LocationPageProps,
	};
};

export const getServerSideProps: GetServerSideProps = async context => {
	const { res } = context;
	const params = context.params.params as string[];
	const userAgent = context.req.headers['user-agent']
	const isBot = checkUserAgentIsBot(userAgent);

	res.setHeader('Cache-Control', `public, max-age=${isBot ? 0 : 30}, s-maxage=${isBot ? 0 : 60 * 60 * 3}, stale-while-revalidate=59`);

	const penultimateParam = params[params.length - 2];
	const lastParam = params[params.length - 1];

	// path like /penultimateParam/lastParam
	if (penultimateParam) {
		// /event-alias/instance-date
		if (!isNaN(Date.parse(lastParam))) {
			return preloadEventData(penultimateParam, lastParam, isBot);
		}

		// /username/events or /username/membership
		if (Object.values(HostTabsEnum).includes(lastParam as HostTabsEnum)) {
			return preloadHostData(penultimateParam, isBot, lastParam as HostTabsEnum);
		}

		// /event-alias/tickets or /event-alias/instance-date/tickets
		if (lastParam === EventTabsEnum.TICKETS) {
			// /event-alias/instance-date/tickets
			if (!isNaN(Date.parse(penultimateParam))) {
				const eventAlias = params[params.length - 3];

				if (eventAlias) {
					return preloadTicketsData(eventAlias, penultimateParam, lastParam);
				}
			}

			// /event-alias/tickets
			const { eventAlias, shortLocalInstance } = await getInstanceOfEventByAlias(penultimateParam);

			if (eventAlias && shortLocalInstance) {
				return preloadTicketsData(eventAlias, shortLocalInstance, lastParam);
			}
		}
	}

	const { foundLocation, cityId, isOtherLocation } = getLocationData({ locationPath: lastParam });

	const mightBeEventOrCommunity =
		foundLocation !== null && isOtherLocation && typeof lastParam === 'string' && lastParam !== 'undefined';

	const communityId = mightBeEventOrCommunity ? await usersService.findPublicIdByUsername(lastParam) : null;

	// if location slug is present, and it is not known ('others') location and if user is found by username, redirect to user profile
	if (mightBeEventOrCommunity && communityId) {
		// /username
		return preloadHostData(lastParam, isBot);
	}

	// if location slug is present, and it is not known ('others') location and if event is found by alias, redirect to the event page
	if (mightBeEventOrCommunity) {
		// /event-alias
		const { eventAlias, shortLocalInstance } = await getInstanceOfEventByAlias(lastParam);

		if (eventAlias && shortLocalInstance) {
			return preloadEventData(eventAlias, shortLocalInstance, isBot);
		}
	}

	// /events/location-slug
	return {
		props: prepareNextJsProps({
			locationCityId: cityId, // pass if we want to sync client and server location
		}) satisfies LocationPageProps,
	};
};

export interface LocationPageProps {
	locationCityId?: string;
	eventId?: string;
	eventBaseId?: string;
	instance?: string;
	eventAlias?: string;
	shortLocalInstance?: string;
	userId?: string;
	userName?: string;
	defaultHostTab?: HostTabsEnum;
	defaultEventTab?: EventTabsEnum;
	disableCloseCheckout?: boolean;
	seo?: OGMetaProps;
	isBot?: boolean;
}

const LocationPage: FC<LocationPageProps> = props => {
	const { eventAlias, userName, shortLocalInstance, defaultHostTab, defaultEventTab, isBot } = props;

	if (eventAlias && shortLocalInstance) {
		if (defaultEventTab === EventTabsEnum.TICKETS) {
			return (
				<EventTicketsPage
					eventAlias={eventAlias}
					shortLocalInstance={shortLocalInstance}
					defaultEventTab={defaultEventTab}
				/>
			);
		}

		return <EventPage eventAlias={eventAlias} shortLocalInstance={shortLocalInstance} isBot={isBot} />;
	}

	if (userName) {
		return <HostPage userName={userName} defaultHostTab={defaultHostTab} isBot={isBot} />;
	}

	return <EventsPage />;
};

export default LocationPage;
